import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Settings as SettingsIcon, User, Bell, Lock, Key, Shield, Save, Upload } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    practice: '',
    specialty: '',
    phone: '',
    address: '',
    bio: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64Image = reader.result;
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/avatar`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ avatar: base64Image })
        });

        const data = await response.json();
        if (data.success) {
          alert('Profile photo updated successfully!');
          window.location.reload();
        } else {
          alert('Failed to upload photo');
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        alert('Error uploading photo');
      }
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        practice: user.practice || '',
        specialty: user.specialty || '',
        phone: '',
        address: '',
        bio: ''
      });
    }
  }, [user]);

  const tabs = [
    { id: 'profile', name: 'Profile', icon: <User className="h-5 w-5" /> },
    { id: 'security', name: 'Security', icon: <Lock className="h-5 w-5" /> },
    { id: 'notifications', name: 'Notifications', icon: <Bell className="h-5 w-5" /> },
    { id: 'api', name: 'API Keys', icon: <Key className="h-5 w-5" /> },
    { id: 'privacy', name: 'Privacy', icon: <Shield className="h-5 w-5" /> }
  ];

  const handleSaveProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(profileData)
      });

      const data = await response.json();
      if (data.success) {
        alert('Profile updated successfully!');
        // Refresh page to update user data
        window.location.reload();
      } else {
        alert('Failed to update profile: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile. Please try again.');
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Password changed successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        alert(data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password');
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
          <SettingsIcon className="h-8 w-8 mr-3 text-[#D4AF37]" />
          Settings
        </h1>
        <p className="text-gray-600">Manage your account and preferences</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <CardContent className="p-4 space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition ${
                    activeTab === tab.id
                      ? 'bg-[#0A1628] text-[#D4AF37]'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {tab.icon}
                  <span className="font-medium">{tab.name}</span>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-3">
          {activeTab === 'profile' && (
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center space-x-6">
                  <Avatar className="h-24 w-24 border-4 border-[#D4AF37]">
                    <AvatarFallback className="bg-[#0A1628] text-[#D4AF37] text-3xl font-bold">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0A1628] mb-1">{user?.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{user?.email}</p>
                    <input
                      type="file"
                      id="photoUpload"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <label htmlFor="photoUpload">
                      <Button as="span" variant="outline" size="sm" className="cursor-pointer">
                        <Upload className="h-4 w-4 mr-2" />
                        Change Photo
                      </Button>
                    </label>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Full Name</label>
                    <Input
                      value={profileData.name}
                      onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <Input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Practice Name</label>
                    <Input
                      value={profileData.practice}
                      onChange={(e) => setProfileData({...profileData, practice: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Specialty</label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={profileData.specialty}
                      onChange={(e) => setProfileData({...profileData, specialty: e.target.value})}
                    >
                      <option value="">Select Specialty</option>
                      <option value="General Dentist">General Dentist</option>
                      <option value="Orthodontist">Orthodontist</option>
                      <option value="Periodontist">Periodontist</option>
                      <option value="Endodontist">Endodontist</option>
                      <option value="Oral Surgeon">Oral Surgeon</option>
                      <option value="Pediatric Dentist">Pediatric Dentist</option>
                      <option value="Prosthodontist">Prosthodontist</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <Input
                      placeholder="(555) 123-4567"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Address</label>
                    <Input
                      placeholder="Office address"
                      value={profileData.address}
                      onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Bio</label>
                  <Textarea
                    placeholder="Tell us about yourself..."
                    rows={4}
                    value={profileData.bio}
                    onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                  />
                </div>

                <div className="flex justify-end">
                  <Button 
                    className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                    onClick={handleSaveProfile}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'security' && (
            <Card>
              <CardHeader>
                <CardTitle>Security Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold text-[#0A1628] mb-4">Change Password</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Current Password</label>
                      <Input
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">New Password</label>
                      <Input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                      <Input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      />
                    </div>
                    <Button 
                      className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
                      onClick={handleChangePassword}
                    >
                      Update Password
                    </Button>
                  </div>
                </div>

                <div className="pt-6 border-t">
                  <h3 className="font-semibold text-[#0A1628] mb-3">Two-Factor Authentication</h3>
                  <p className="text-sm text-gray-600 mb-4">Add an extra layer of security to your account</p>
                  <Button variant="outline">Enable 2FA</Button>
                </div>

                <div className="pt-6 border-t">
                  <h3 className="font-semibold text-[#0A1628] mb-3">Active Sessions</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium text-sm">Current Device</p>
                        <p className="text-xs text-gray-600">Last active: Just now</p>
                      </div>
                      <Button variant="outline" size="sm" disabled>Current</Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'notifications' && (
            <Card>
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  ['Email Notifications', 'Receive email updates'],
                  ['New Referrals', 'Get notified of new referrals'],
                  ['Messages', 'Alerts for new messages'],
                  ['System Updates', 'Platform updates and maintenance']
                ].map(([title, desc]) => (
                  <div key={title} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">{title}</p>
                      <p className="text-sm text-gray-600">{desc}</p>
                    </div>
                    <input type="checkbox" defaultChecked className="h-5 w-5 rounded" />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {activeTab === 'api' && (
            <Card>
              <CardHeader>
                <CardTitle>API Keys</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">Manage your API keys for integrations</p>
                <div className="space-y-3">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Production Key</p>
                    <code className="text-xs bg-white p-2 rounded border block">sk_live_••••••••••••••••</code>
                  </div>
                  <Button variant="outline">Generate New Key</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'privacy' && (
            <Card>
              <CardHeader>
                <CardTitle>Privacy & Data</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-3">HIPAA Compliance</h3>
                  <p className="text-sm text-gray-600 mb-4">Your data is protected under HIPAA regulations</p>
                  <Button variant="outline">View Audit Log</Button>
                </div>
                <div className="pt-6 border-t">
                  <h3 className="font-semibold mb-3">Data Export</h3>
                  <p className="text-sm text-gray-600 mb-4">Download all your personal data</p>
                  <Button variant="outline">Export My Data</Button>
                </div>
                <div className="pt-6 border-t">
                  <h3 className="font-semibold text-red-600 mb-3">Delete Account</h3>
                  <p className="text-sm text-gray-600 mb-4">Permanently delete your account and all data</p>
                  <Button variant="outline" className="border-red-500 text-red-500 hover:bg-red-50">
                    Delete Account
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
